import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "./frp-ag-grid.css";
import { AgGridReact } from "ag-grid-react";
import PropTypes from "prop-types";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createLogger } from "../../helper/logger";
import CustomPagination from "./custom-pagination/CustomPagination";

const defaultColDef = {
	flex: 1,
	width: 100,
	editable: false,
	suppressMovable: true,
	floatingFilter: false,
	tooltipComponentParams: { color: "#ececec" },
	cellStyle: {
		whiteSpace: "normal",
		wordWrap: "break-word",
		lineHeight: "1.75",
		fontSize: "0.875rem",
	},
};

const FRPAgGrid = ({
	columnDefs,
	rowData = null,
	datasource = null,
	cacheBlockSize = 100,
	onGridReady,
	onRowDataUpdated,
	frameworkComponents = {},
	gridRef,
	getRowId,
	height = "medium", // new prop: "small", "medium", "large", "auto", or a custom CSS height value
	onPageSizeChange,
}) => {
	const logger = useMemo(() => createLogger("FRPAgGrid"), []);
	const gridApiRef = useRef(null);
	const hiddenInputRef = useRef(null);
	const scrollCleanupRef = useRef(null);

	// Pagination state
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalItems, setTotalItems] = useState(0);
	const [pageSize, setPageSize] = useState(cacheBlockSize);
	const [loading, setLoading] = useState(false);
	// Custom datasource for pagination
	const [paginatedData, setPaginatedData] = useState(null);

	// Handle page size changes
	const handlePageSizeChange = useCallback(
		(newSize) => {
			setPageSize(newSize);
			// Reset to first page when changing page size
			setCurrentPage(1);
			if (onPageSizeChange) {
				onPageSizeChange(newSize);
			}
			// Refetch data with new page size if we have a datasource
			if (datasource) {
				setLoading(true);
				datasource.getRows({
					startRow: 0,
					endRow: newSize,
					successCallback: (rowData, rowCount) => {
						setPaginatedData(rowData || []);
						setTotalItems(rowCount || 0);
						setTotalPages(Math.ceil((rowCount || 0) / newSize));
						setLoading(false);
					},
					failCallback: () => {
						logger.error("Failed to fetch data after page size change");
						setLoading(false);
						setPaginatedData([]);
					},
				});
			}
		},
		[datasource, onPageSizeChange, logger],
	);

	// Handle body scroll to blur inputs
	const handleBodyScroll = useCallback(() => {
		// Focus the hidden input to trigger blur on all other inputs
		if (hiddenInputRef.current) {
			hiddenInputRef.current.focus();
		}
	}, []);

	// Handle page changes
	const handlePageChange = useCallback(
		(page) => {
			if (!datasource || !gridApiRef.current) {
				logger.warn("Attempted to change page without datasource or gridApi");
				setLoading(false);
				return;
			}

			setCurrentPage(page);
			setLoading(true);

			const startRow = (page - 1) * pageSize;
			const endRow = page * pageSize;

			// Call the datasource to get rows for the requested page
			datasource.getRows({
				startRow,
				endRow,
				successCallback: (rowData, rowCount) => {
					setPaginatedData(rowData || []);
					setTotalItems(rowCount || 0);
					setTotalPages(Math.ceil((rowCount || 0) / pageSize));
					setLoading(false);
				},
				failCallback: () => {
					logger.error("Failed to fetch page data");
					setLoading(false);
					// Set empty data to avoid rendering issues
					setPaginatedData([]);
				},
			});
		},
		[datasource, pageSize, logger],
	);
	const onGridReadyHandler = useCallback(
		(params) => {
			gridApiRef.current = params.api; // Set up body scroll listener

			// If we don't have a datasource, just call the callback and return
			if (!datasource) {
				if (onGridReady) {
					onGridReady(params);
				}
				return;
			}

			// Initial data fetch for the first page
			setLoading(true);
			datasource.getRows({
				startRow: 0,
				endRow: pageSize,
				successCallback: (rowData, rowCount) => {
					setPaginatedData(rowData);
					setTotalItems(rowCount || 0);
					setTotalPages(Math.ceil((rowCount || 0) / pageSize));
					setLoading(false);
				},
				failCallback: () => {
					logger.error("Failed to fetch initial data");
					setPaginatedData([]);
					setTotalItems(0);
					setTotalPages(0);
					setLoading(false);
				},
			});

			// If props has onGridReady callback, call it
			if (onGridReady) {
				onGridReady(params);
			}
		},
		[datasource, onGridReady, pageSize, logger],
	);

	// Effect to update datasource if it changes after initial render
	useEffect(
		function onDatasourceChange() {
			if (!gridApiRef.current) return;

			if (datasource) {
				// Reset pagination and fetch first page of data
				setCurrentPage(1);
				handlePageChange(1);
			} else {
				// Clear pagination data if datasource is removed
				setPaginatedData(null);
				setTotalItems(0);
				setTotalPages(0);
			}
		},
		[datasource, handlePageChange],
	);

	// Cleanup scroll listener on unmount
	useEffect(() => {
		return () => {
			if (scrollCleanupRef.current) {
				scrollCleanupRef.current();
			}
		};
	}, []);

	// Determine container class and style based on height prop
	const containerClass = useMemo(() => {
		const baseClass = "frp-ag-grid-container";
		if (["small", "medium", "large", "auto"].includes(height)) {
			return `${baseClass} ${height}`;
		}
		return baseClass;
	}, [height]);

	const containerStyle = useMemo(() => {
		if (!["small", "medium", "large", "auto"].includes(height)) {
			// Custom height value provided
			return { height };
		}
		return {};
	}, [height]);

	logger.debug("Rendering grid with props", {
		hasRowData: !!rowData,
		rowDataLength: rowData?.length || 0,
		columnDefsCount: columnDefs?.length || 0,
		isInfinite: !!datasource,
		currentPage,
		totalPages,
		totalItems,
	});
	return (
		<div className={containerClass} style={containerStyle}>
			{" "}
			{/* Hidden input for focus management during scroll */}
			<input
				ref={hiddenInputRef}
				style={{
					position: "absolute",
					left: "-9999px",
					width: "1px",
					height: "1px",
					opacity: 0,
					pointerEvents: "none",
				}}
				tabIndex={-1}
				aria-label="Focus management helper"
			/>
			<div className={`frp-ag-grid ${loading ? "loading" : ""}`}>
				<AgGridReact
					ref={gridRef}
					columnDefs={columnDefs}
					rowData={datasource ? paginatedData : rowData}
					defaultColDef={defaultColDef}
					frameworkComponents={frameworkComponents}
					tooltipShowDelay={0}
					// Grid properties
					getRowId={getRowId}
					onBodyScroll={handleBodyScroll}
					// Handlers
					onGridReady={onGridReadyHandler}
					onRowDataUpdated={(event) => {
						logger.debug("Row data updated", {
							hasData: !!event.api.getDisplayedRowCount(),
							rowCount: event.api.getDisplayedRowCount(),
						});
						if (onRowDataUpdated) {
							onRowDataUpdated(event);
						}
					}}
				/>
				{loading && (
					<div className="grid-loading-overlay">
						<div className="grid-loading-spinner" />
					</div>
				)}
			</div>{" "}
			{datasource && totalPages > 0 && (
				<CustomPagination
					currentPage={currentPage}
					totalPages={totalPages}
					onPageChange={handlePageChange}
					totalItems={totalItems}
					pageSize={pageSize}
					onPageSizeChange={handlePageSizeChange}
				/>
			)}
		</div>
	);
};

FRPAgGrid.propTypes = {
	// Grid configuration
	columnDefs: PropTypes.array.isRequired,
	rowData: PropTypes.array,
	datasource: PropTypes.shape({
		getRows: PropTypes.func.isRequired,
	}),
	cacheBlockSize: PropTypes.number,
	// Grid callbacks
	onGridReady: PropTypes.func,
	onRowDataUpdated: PropTypes.func,
	onPageSizeChange: PropTypes.func,

	// Grid customization
	frameworkComponents: PropTypes.object,
	gridRef: PropTypes.object,
	getRowId: PropTypes.func,

	// Layout
	height: PropTypes.oneOfType([
		PropTypes.oneOf(["small", "medium", "large", "auto"]),
		PropTypes.string,
	]),
};

export default memo(FRPAgGrid, (prevProps, nextProps) => {
	const areEqual =
		prevProps.rowData === nextProps.rowData &&
		prevProps.columnDefs === nextProps.columnDefs &&
		prevProps.datasource === nextProps.datasource;
	return areEqual;
});
